import React, {ChangeEvent, forwardRef, useEffect, useRef, useState} from "react";
import {Loader} from "components";
import {useOnClickOutside} from "hooks";
import classNames from "classnames";
import colors from "styles/themes.module.scss";
import SearchInput from "mapx-components/Inputs/SearchInput";
import {SuggestionsListComponent} from "mapx-components/Inputs/AutoComplete/SuggestionsListComponent";
import SuggestionListSkeleton from "mapx-components/Inputs/AutoComplete/SuggestionsListComponent/SuggestionListSkeleton";
import styles from "./autoComplete.module.scss";
import css from "../SearchInput/searchInput.module.scss";
import {AutoCompleteProps} from "mapx-components/Inputs/AutoComplete/types";

// eslint-disable-next-line react/display-name
const AutoComplete = forwardRef(
	<T,>(
		{
			suggestions = [],
			notFoundResult = "No results found",
			handleFilterChange,
			handleItemClick,
			isLoading = false,
			loaderColor = colors.loaderDotColor,
			SuggestionListItemComponent,
			hasMore = false,
			disableFocus = false,
			placeholderText = "Search",
			clearInputOnSelect = true,
			defaultValue = "",
			...rest
		}: AutoCompleteProps<T>,
		ref: React.Ref<HTMLDivElement>,
	) => {
		const searchRef = useRef<HTMLDivElement>(null);
		const [input, setInput] = useState<string>(defaultValue);
		const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
		const [internalLoading, setInternalLoading] = useState<boolean>(false);

		// Turn off internal loader once external isLoading goes false
		useEffect(() => {
			if (internalLoading && !isLoading) {
				setInternalLoading(false);
			}
		}, [isLoading, internalLoading]);

		const onChange = (e: ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setInput(val);

			handleFilterChange(val);

			if (val.length > 1) {
				setShowSuggestions(true);
				setInternalLoading(true);
			}
		};

		const onClickItem = (item: T) => {
			handleItemClick(item);

			setShowSuggestions(false);

			if (clearInputOnSelect) {
				setInput("");
			}
		};

		const InlineLoaderComponent = (
			<Loader
				width={20}
				height={20}
				type="ThreeDots"
				color={loaderColor}
				displayAtCenterOfPage={false}
			/>
		);

		const renderResults =
			isLoading && suggestions.length === 0 ? (
				<SuggestionListSkeleton />
			) : suggestions.length > 0 ? (
				<SuggestionsListComponent
					ref={ref}
					onClick={onClickItem}
					displayLoader={hasMore}
					suggestions={suggestions}
					SuggestionListItemComponent={SuggestionListItemComponent}
				/>
			) : (
				input.length > 1 && (
					<div className={styles.list__empty}>
						<p className={styles.list__empty_text}>{notFoundResult}</p>
					</div>
				)
			);

		useOnClickOutside(searchRef, () => setShowSuggestions(false));

		return (
			<div ref={searchRef} className={styles.container}>
				<div className={styles.searchWrapper}>
					<SearchInput
						type="text"
						value={input}
						autoComplete="off"
						onChange={onChange}
						className={classNames(css.searchInput, {
							[css.disableFocus]: disableFocus,
						})}
						placeholder={placeholderText}
						onFocus={() => setShowSuggestions(true)}
						{...rest}
					/>
					{isLoading && <span className={styles.loader}>{InlineLoaderComponent}</span>}
				</div>

				{showSuggestions && input.length > 0 && (
					<div className={styles.list}>
						{renderResults}

						{hasMore && (
							<div
								ref={ref}
								style={{width: "100%", height: 1, visibility: "hidden"}}
							/>
						)}
					</div>
				)}
			</div>
		);
	},
) as <T>(props: AutoCompleteProps<T> & {ref?: React.Ref<HTMLDivElement>}) => React.ReactElement;

// AutoComplete.displayName = "AutoComplete";

export default AutoComplete;
